import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog, Box, Typography, Button, IconButton, ButtonBase,
  TextField, InputAdornment, Chip, alpha, useMediaQuery,
  CircularProgress, Divider,
} from '@mui/material';
import {
  Close, Search, SwapHoriz, CheckCircle,
  Male, Female, ArrowForward, ChevronRight,
  SportsTennis, TrendingUp,
} from '@mui/icons-material';
import { colors } from '../../theme/colors';
import { tennisSquadService } from '../../services/firestore';
import { flagEmoji } from '../../utils/countryFlags';
import {
  applyTennisPlayerSwap,
  applyTennisPositionSwap,
  validateTennisPlayerSwap,
  validateTennisPositionSwap,
  computeTennisTransferPreviewBudget,
} from '../../utils/tennisTransfers';
import { buildTennisTransferHistoryEntry } from '../../utils/adminTennisTransfer';
import type { League, LeagueSquad } from '../../types/database';
import type { PlayerPoolEntry } from '../../utils/tennisTransfers';

// ── Types ────────────────────────────────────────────────────────────────────

type TransferType = 'playerSwap' | 'positionSwap';
type Step = 'chooseType' | 'selectSlot' | 'selectPlayer' | 'selectPositions' | 'confirm';

interface TennisTransferModalProps {
  open: boolean;
  onClose: () => void;
  league: League;
  existingSquad: LeagueSquad;
  tennisPool: PlayerPoolEntry[];
  onTransferComplete: (updatedSquad: LeagueSquad) => void;
  /**
   * Set when an admin is performing the transfer on behalf of the squad owner.
   * - `actingUid` is stamped on the resulting transferHistory entry for audit.
   * - `targetUserName` is shown in the modal header so the admin can verify
   *   they're acting on the right squad.
   * Undefined for the regular (player-initiated) flow.
   */
  adminContext?: { actingUid: string; targetUserName?: string };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const glassCard = (accent: string) => ({
  background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.96)}, ${alpha('#0A1929', 0.99)})`,
  border: `1px solid ${alpha(accent, 0.22)}`,
  borderRadius: 2.5,
  overflow: 'hidden' as const,
  position: 'relative' as const,
  '&::before': {
    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
    background: `linear-gradient(90deg, transparent, ${alpha(accent, 0.7)}, transparent)`,
  },
});

// Stable solid CTA color — no gradient, clear affordance
const ctaButton = (accent: string, disabled?: boolean) => ({
  bgcolor: disabled ? alpha(accent, 0.25) : accent,
  color: '#fff',
  textTransform: 'none' as const,
  fontWeight: 700,
  fontFamily: "'Satoshi', sans-serif",
  borderRadius: 2.5,
  fontSize: '0.92rem',
  letterSpacing: '0.01em',
  boxShadow: disabled ? 'none' : `0 4px 16px ${alpha(accent, 0.35)}`,
  transition: 'all 0.18s ease',
  '&:hover:not(:disabled)': {
    bgcolor: accent,
    filter: 'brightness(1.12)',
    boxShadow: `0 6px 20px ${alpha(accent, 0.45)}`,
    transform: 'translateY(-1px)',
  },
  '&:active:not(:disabled)': { transform: 'translateY(0)', filter: 'brightness(0.96)' },
  '&.Mui-disabled': { bgcolor: alpha(accent, 0.18), color: alpha('#fff', 0.35), boxShadow: 'none' },
});

// ── Main Component ───────────────────────────────────────────────────────────

const TennisTransferModal: React.FC<TennisTransferModalProps> = ({
  open, onClose, league, existingSquad, tennisPool, onTransferComplete, adminContext,
}) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const menColor   = colors.blue.electric;
  const womenColor = '#E91E63';

  const menSlots     = league.tennisSquadRules?.menSlots     ?? 5;
  const womenSlots   = league.tennisSquadRules?.womenSlots   ?? 5;
  const menMults     = league.tennisSquadRules?.menMultipliers   ?? [2, 1.75, 1.5, 1.25, 1];
  const womenMults   = league.tennisSquadRules?.womenMultipliers ?? [2, 1.75, 1.5, 1.25, 1];
  const budget       = Number(league.tennisSquadRules?.budget ?? 0);
  const hasBudget    = budget > 0;

  const maxTransfers    = Number(league.tennisSquadRules?.maxTransfers ?? 4);
  const transfersUsed   = Number(existingSquad.transfersUsed ?? 0);
  const transfersLeft   = maxTransfers > 0 ? maxTransfers - transfersUsed : Infinity;
  const noTransfersLeft = isFinite(transfersLeft) && transfersLeft <= 0;

  const [step,          setStep]          = useState<Step>('chooseType');
  const [transferType,  setTransferType]  = useState<TransferType>('playerSwap');
  const [gender,        setGender]        = useState<'men' | 'women'>('men');
  const [targetSlot,    setTargetSlot]    = useState<number | null>(null);
  const [swapSlotA,     setSwapSlotA]     = useState<number | null>(null);
  const [swapSlotB,     setSwapSlotB]     = useState<number | null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerPoolEntry | null>(null);
  const [search,        setSearch]        = useState('');
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState('');

  // Derived slot/player data
  const squadPlayers = existingSquad.players;

  const playerAt = useCallback((g: 'men' | 'women', slot: number) =>
    squadPlayers.find(p => p.gender === g && p.slotIndex === slot) ?? null,
  [squadPlayers]);

  const accentColor = gender === 'men' ? menColor : womenColor;
  const multipliers = gender === 'men' ? menMults : womenMults;
  const slotCount   = gender === 'men' ? menSlots  : womenSlots;
  const slotLabel   = (g: 'men' | 'women', i: number) => `${g === 'men' ? 'M' : 'W'}${i + 1}`;

  // Current spend for budget display (with pool-price fallback for legacy squads
  // whose SquadPlayer.price was never persisted). previewBudget is computed via
  // the shared pure helper so both currentSpend and outgoing.price use the same
  // fallback — without this, swaps on legacy squads falsely tripped isOverBudget.
  const previewMath = useMemo(() => {
    if (selectedPlayer && targetSlot !== null) {
      return computeTennisTransferPreviewBudget({
        squadPlayers, tennisPool,
        outgoingSlot: { gender, slotIndex: targetSlot },
        incomingPlayer: selectedPlayer,
      });
    }
    // No selection yet — only currentSpend matters; reuse the helper with a
    // dummy outgoing slot so we get the same fallback semantics.
    const fallback = computeTennisTransferPreviewBudget({
      squadPlayers, tennisPool,
      outgoingSlot: { gender, slotIndex: -1 },
      incomingPlayer: { playerId: '', name: '', team: '', points: 0, price: 0 } as PlayerPoolEntry,
    });
    return { currentSpend: fallback.currentSpend, previewBudget: fallback.currentSpend };
  }, [squadPlayers, tennisPool, selectedPlayer, targetSlot, gender]);
  const currentSpend = previewMath.currentSpend;

  // Pool filtered by gender, excluding players already in squad
  const eligiblePool = useMemo(() => {
    const squadIds = new Set(squadPlayers.map(p => p.playerId));
    return tennisPool
      .filter(p => (p.gender === gender || p.role === gender) && !squadIds.has(p.playerId))
      .sort((a, b) => {
        if (a.seed !== undefined && b.seed !== undefined) return a.seed - b.seed;
        if (a.seed !== undefined) return -1;
        if (b.seed !== undefined) return 1;
        return b.points - a.points;
      });
  }, [tennisPool, gender, squadPlayers]);

  const filteredPool = useMemo(() => {
    if (!search) return eligiblePool;
    const q = search.toLowerCase();
    return eligiblePool.filter(p =>
      p.name.toLowerCase().includes(q) || (p.team ?? '').toLowerCase().includes(q)
    );
  }, [eligiblePool, search]);

  // Budget after swap preview (computed inside previewMath above so outgoing
  // price uses the same pool fallback as currentSpend).
  const previewBudget = previewMath.previewBudget;

  const isOverBudget = hasBudget && previewBudget > budget;

  // ── Reset ──────────────────────────────────────────────────────────────────
  const reset = () => {
    setStep('chooseType');
    setTransferType('playerSwap');
    setGender('men');
    setTargetSlot(null);
    setSwapSlotA(null);
    setSwapSlotB(null);
    setSelectedPlayer(null);
    setSearch('');
    setError('');
  };

  const handleClose = () => { reset(); onClose(); };

  // ── Confirm transfer ───────────────────────────────────────────────────────
  const handleConfirm = async () => {
    setError('');
    if (noTransfersLeft) {
      setError('Transfer limit reached. No transfers remaining.');
      return;
    }
    setSaving(true);
    try {
      let result: { updatedPlayers: typeof squadPlayers; bankedDelta: number };

      // Enrich squad players with pool prices for legacy squads that lack stored prices
      const enrichedPlayers = squadPlayers.map(p => ({
        ...p,
        price: p.price ?? tennisPool.find(x => x.playerId === p.playerId)?.price,
      }));

      if (transferType === 'playerSwap' && targetSlot !== null && selectedPlayer) {
        const validation = validateTennisPlayerSwap({
          players: enrichedPlayers, gender, slotIndex: targetSlot,
          incomingPlayerId: selectedPlayer.playerId,
          incomingPrice: selectedPlayer.price ?? 0,
          budget,
        });
        if (!validation.valid) { setError(validation.error ?? 'Invalid transfer.'); setSaving(false); return; }

        result = applyTennisPlayerSwap({
          players: enrichedPlayers, gender, slotIndex: targetSlot,
          incomingPlayer: selectedPlayer, multipliers,
        });
      } else if (transferType === 'positionSwap' && swapSlotA !== null && swapSlotB !== null) {
        const validation = validateTennisPositionSwap({
          players: enrichedPlayers, gender, slotA: swapSlotA, slotB: swapSlotB,
        });
        if (!validation.valid) { setError(validation.error ?? 'Invalid swap.'); setSaving(false); return; }

        result = applyTennisPositionSwap({
          players: enrichedPlayers, gender, slotA: swapSlotA, slotB: swapSlotB, multipliers,
        });
      } else {
        setError('Incomplete transfer selection.'); setSaving(false); return;
      }

      const previousBanked = existingSquad.bankedPoints ?? 0;
      const newBanked = previousBanked + result.bankedDelta;

      const historyEntry =
        transferType === 'playerSwap' && targetSlot !== null && selectedPlayer
          ? buildTennisTransferHistoryEntry({
              transferType: 'playerSwap',
              gender,
              outgoingPlayer: playerAt(gender, targetSlot),
              incomingPlayer: selectedPlayer,
              bankedDelta: result.bankedDelta,
              previousBanked,
              pointsBefore: existingSquad.totalPoints,
              ...(adminContext ? { actedByAdminUid: adminContext.actingUid } : {}),
            })
          : buildTennisTransferHistoryEntry({
              transferType: 'positionSwap',
              gender,
              slotA: swapSlotA as number,
              slotB: swapSlotB as number,
              bankedDelta: result.bankedDelta,
              previousBanked,
              pointsBefore: existingSquad.totalPoints,
              ...(adminContext ? { actedByAdminUid: adminContext.actingUid } : {}),
            });

      const updatePayload = {
        players: result.updatedPlayers,
        bankedPoints: newBanked,
        transfersUsed: (existingSquad.transfersUsed ?? 0) + 1,
        transferHistory: [...(existingSquad.transferHistory ?? []), historyEntry],
        lastUpdated: new Date(),
      };

      await tennisSquadService.update(existingSquad.id, updatePayload);
      onTransferComplete({ ...existingSquad, ...updatePayload });
      reset();
    } catch (e: any) {
      setError(e?.message ?? 'Transfer failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── SlotCard (used in slot selection steps) ────────────────────────────────
  const SlotCard = ({
    g, idx, selected, dimmed, onTap,
  }: { g: 'men' | 'women'; idx: number; selected?: boolean; dimmed?: boolean; onTap: () => void }) => {
    const p = playerAt(g, idx);
    const accent = g === 'men' ? menColor : womenColor;
    const mult   = (g === 'men' ? menMults : womenMults)[idx];
    const resolvedPrice = p ? (p.price ?? tennisPool.find(x => x.playerId === p.playerId)?.price) : undefined;
    return (
      <ButtonBase
        onClick={onTap}
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 1.5, py: 1.25, borderRadius: 2.5,
          border: `1px solid ${selected ? alpha(accent, 0.65) : alpha(accent, 0.14)}`,
          bgcolor: selected ? alpha(accent, 0.1) : 'transparent',
          opacity: dimmed ? 0.35 : 1,
          transition: 'all 0.15s ease',
          '&:hover:not(:disabled)': { border: `1px solid ${alpha(accent, 0.45)}`, bgcolor: alpha(accent, 0.06) },
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
          {/* Multiplier badge */}
          <Box sx={{
            width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
            bgcolor: selected ? alpha(accent, 0.2) : alpha(accent, 0.08),
            border: `1.5px solid ${alpha(accent, selected ? 0.7 : 0.25)}`,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: alpha(accent, 0.7), lineHeight: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {slotLabel(g, idx)}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: accent, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1.2 }}>
              {mult}x
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            {p ? (
              <>
                <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: colors.text.primary, lineHeight: 1.25 }} noWrap>
                  {p.playerName}
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: alpha(colors.text.secondary, 0.6), mt: 0.1 }}>
                  {flagEmoji(p.team)} {p.team}{resolvedPrice ? ` · ${resolvedPrice}cr` : ''}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: '0.82rem', color: alpha(colors.text.secondary, 0.28), fontFamily: "'Satoshi', sans-serif", fontStyle: 'italic' }}>
                Empty slot
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ flexShrink: 0, ml: 1 }}>
          {selected
            ? <CheckCircle sx={{ fontSize: 18, color: accent }} />
            : <ChevronRight sx={{ fontSize: 18, color: alpha(accent, 0.35) }} />
          }
        </Box>
      </ButtonBase>
    );
  };

  // ── Render steps ───────────────────────────────────────────────────────────

  const StepHeader = ({ title, subtitle, accent }: { title: string; subtitle?: string; accent?: string }) => {
    const a = accent ?? accentColor;
    return (
      <Box sx={{
        px: { xs: 2, sm: 2.5 }, pt: { xs: 2.25, sm: 2.5 }, pb: 1.75,
        borderBottom: `1px solid rgba(255,255,255,0.05)`, flexShrink: 0,
        position: 'relative',
      }}>
        {/* Subtle top accent line */}
        <Box sx={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1.5px', borderRadius: '0 0 4px 4px', bgcolor: alpha(a, 0.45) }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' }, color: colors.text.primary, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.5), mt: 0.3, lineHeight: 1.4 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={handleClose}
            sx={{ color: alpha(colors.text.secondary, 0.4), mt: -0.25, flexShrink: 0,
              '&:hover': { color: colors.text.primary, bgcolor: alpha('#fff', 0.06) } }}>
            <Close sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderChooseType = () => (
    <>
      <StepHeader
        title={adminContext ? `Admin Transfer — ${adminContext.targetUserName ?? 'on behalf'}` : 'Make a Transfer'}
        subtitle={adminContext ? 'Acting on behalf of squad owner' : 'Choose what change you want to make'}
      />
      {/* Transfers remaining pill */}
      {maxTransfers > 0 && (
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.25, pb: 0.25 }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.25, py: 0.5, borderRadius: 10,
            bgcolor: noTransfersLeft ? alpha('#EF4444', 0.1) : alpha(colors.success.primary, 0.09),
            border: `1px solid ${noTransfersLeft ? alpha('#EF4444', 0.28) : alpha(colors.success.primary, 0.22)}`,
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: noTransfersLeft ? '#EF4444' : colors.success.primary }} />
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: noTransfersLeft ? '#EF4444' : colors.success.primary, fontFamily: "'Satoshi', sans-serif" }}>
              {noTransfersLeft ? 'No transfers left' : `${transfersLeft} of ${maxTransfers} transfers remaining`}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, overflowY: 'auto' }}>
        {(['men', 'women'] as const).map((g, gi) => {
          const c = g === 'men' ? menColor : womenColor;
          return (
            <Box key={g}>
              {gi > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)', my: 2 }} />}
              {/* Section label */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                {g === 'men' ? <Male sx={{ fontSize: 14, color: c }} /> : <Female sx={{ fontSize: 14, color: c }} />}
                <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: alpha(c, 0.75), textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  {g === 'men' ? "Men's Draw" : "Women's Draw"}
                </Typography>
              </Box>

              {/* Replace player */}
              <ButtonBase
                onClick={() => { setGender(g); setTransferType('playerSwap'); setStep('selectSlot'); }}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 1.4, mb: 0.75, borderRadius: 2.5,
                  border: `1px solid ${alpha(c, 0.18)}`,
                  bgcolor: alpha(c, 0.04),
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: alpha(c, 0.09), border: `1px solid ${alpha(c, 0.38)}` },
                  '&:active': { transform: 'scale(0.99)' },
                }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.75, flexShrink: 0,
                  bgcolor: alpha(c, 0.1), border: `1px solid ${alpha(c, 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <SportsTennis sx={{ fontSize: 17, color: c }} />
                </Box>
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: colors.text.primary, lineHeight: 1.3 }}>
                    Swap a Player
                  </Typography>
                  <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), mt: 0.2 }}>
                    Replace someone from the pool
                  </Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 18, color: alpha(c, 0.4), flexShrink: 0 }} />
              </ButtonBase>

              {/* Reorder slots */}
              <ButtonBase
                onClick={() => { setGender(g); setTransferType('positionSwap'); setStep('selectPositions'); }}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.5, py: 1.4, borderRadius: 2.5,
                  border: `1px solid ${alpha(c, 0.18)}`,
                  bgcolor: alpha(c, 0.04),
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: alpha(c, 0.09), border: `1px solid ${alpha(c, 0.38)}` },
                  '&:active': { transform: 'scale(0.99)' },
                }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: 1.75, flexShrink: 0,
                  bgcolor: alpha(c, 0.1), border: `1px solid ${alpha(c, 0.25)}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <SwapHoriz sx={{ fontSize: 18, color: c }} />
                </Box>
                <Box sx={{ textAlign: 'left', flex: 1 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: colors.text.primary, lineHeight: 1.3 }}>
                    Reorder Slots
                  </Typography>
                  <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), mt: 0.2 }}>
                    Swap multiplier positions {g === 'men' ? '(e.g. M1 ↔ M3)' : '(e.g. W1 ↔ W3)'}
                  </Typography>
                </Box>
                <ChevronRight sx={{ fontSize: 18, color: alpha(c, 0.4), flexShrink: 0 }} />
              </ButtonBase>
            </Box>
          );
        })}
      </Box>
    </>
  );

  const renderSelectSlot = () => (
    <>
      <StepHeader
        title={`${gender === 'men' ? "Men's" : "Women's"} — Pick Slot`}
        subtitle="Who do you want to replace?"
        accent={accentColor}
      />
      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1.5, flex: 1, overflowY: 'auto' }}>
        {Array.from({ length: slotCount }, (_, i) => (
          <SlotCard
            key={i} g={gender} idx={i}
            selected={targetSlot === i}
            onTap={() => {
              setTargetSlot(i);
              setSelectedPlayer(null);
              setSearch('');
              setStep('selectPlayer');
            }}
          />
        ))}
      </Box>
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Button fullWidth variant="text" onClick={() => setStep('chooseType')}
          sx={{ color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
          ← Back
        </Button>
      </Box>
    </>
  );

  const renderSelectPlayer = () => {
    const outgoing = targetSlot !== null ? playerAt(gender, targetSlot) : null;
    const outgoingPriceN = Number(outgoing?.price ?? tennisPool.find(x => x.playerId === outgoing?.playerId)?.price ?? 0);
    const availableBudget = hasBudget ? budget - Number(currentSpend) + outgoingPriceN : Infinity;
    const maxAffordablePrice = hasBudget && targetSlot !== null ? availableBudget : Infinity;

    return (
      <>
        <StepHeader
          title="Pick Replacement"
          subtitle={outgoing ? `Replacing ${outgoing.playerName} · ${slotLabel(gender, targetSlot!)}` : undefined}
          accent={accentColor}
        />

        {/* Outgoing player + budget bar */}
        {(outgoing || hasBudget) && (
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1.25, pb: 1, borderBottom: '1px solid rgba(255,255,255,0.05)', flexShrink: 0 }}>
            {outgoing && (
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, mb: hasBudget ? 1 : 0,
                px: 1.25, py: 0.9, borderRadius: 2,
                bgcolor: alpha('#EF4444', 0.06), border: `1px solid ${alpha('#EF4444', 0.15)}`,
              }}>
                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#EF4444', flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.7rem', color: alpha('#EF4444', 0.75), fontWeight: 600, fontFamily: "'Satoshi', sans-serif" }}>
                  OUT:
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: colors.text.primary, fontFamily: "'Satoshi', sans-serif", flex: 1 }} noWrap>
                  {outgoing.playerName}
                </Typography>
                {outgoingPriceN > 0 && (
                  <Chip size="small" label={`${outgoingPriceN}cr`}
                    sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha('#EF4444', 0.1), color: alpha('#EF4444', 0.85), border: `1px solid ${alpha('#EF4444', 0.2)}` }} />
                )}
              </Box>
            )}
            {hasBudget && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif" }}>
                  Budget available for incoming
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: availableBudget > 0 ? colors.success.primary : '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>
                  {availableBudget.toFixed(1)}cr
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Search bar */}
        <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, flexShrink: 0 }}>
          <TextField
            fullWidth size="small" placeholder="Search by name or country…"
            value={search} onChange={e => setSearch(e.target.value)}
            autoFocus={!isMobile}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: alpha(colors.text.secondary, 0.35) }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.3 }}>
                    <Close sx={{ fontSize: 13, color: alpha(colors.text.secondary, 0.5) }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                bgcolor: alpha(accentColor, 0.04), borderRadius: 2, fontSize: '0.82rem',
                '& fieldset': { borderColor: alpha(accentColor, 0.15) },
                '&:hover fieldset': { borderColor: alpha(accentColor, 0.3) },
                '&.Mui-focused fieldset': { borderColor: alpha(accentColor, 0.55) },
              },
            }}
          />
          {search && (
            <Typography sx={{ fontSize: '0.62rem', color: alpha(colors.text.secondary, 0.38), mt: 0.5, pl: 0.5 }}>
              {filteredPool.length} result{filteredPool.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* Player list */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: { xs: 1, sm: 1.5 }, pb: 1 }}>
          {filteredPool.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 5 }}>
              <SportsTennis sx={{ fontSize: 28, color: alpha(colors.text.secondary, 0.18), mb: 1 }} />
              <Typography sx={{ color: alpha(colors.text.secondary, 0.35), fontSize: '0.8rem', fontFamily: "'Satoshi', sans-serif" }}>
                {search ? `No results for "${search}"` : 'No players available'}
              </Typography>
            </Box>
          ) : filteredPool.map(p => {
            const overBudget = hasBudget && targetSlot !== null && Number(p.price ?? 0) > maxAffordablePrice;
            const isSelected = selectedPlayer?.playerId === p.playerId;
            const seedColor = p.seed !== undefined
              ? (p.seed <= 4 ? colors.gold : p.seed <= 8 ? colors.orange.primary : alpha(colors.text.secondary, 0.45))
              : null;

            return (
              <ButtonBase
                key={p.playerId}
                onClick={() => !overBudget && setSelectedPlayer(p)}
                disabled={overBudget}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.25, py: 1, borderRadius: 2.5, mb: 0.5,
                  opacity: overBudget ? 0.38 : 1,
                  bgcolor: isSelected ? alpha(accentColor, 0.1) : 'transparent',
                  border: `1px solid ${isSelected ? alpha(accentColor, 0.4) : overBudget ? alpha('#EF4444', 0.08) : 'transparent'}`,
                  transition: 'all 0.13s ease',
                  '&:hover:not(:disabled)': {
                    bgcolor: alpha(accentColor, 0.07),
                    border: `1px solid ${alpha(accentColor, 0.25)}`,
                  },
                  '&:active:not(:disabled)': { transform: 'scale(0.99)' },
                }}
              >
                {/* Left: seed badge + name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: p.seed !== undefined ? alpha(seedColor!, 0.12) : alpha(colors.text.secondary, 0.06),
                    border: `1px solid ${p.seed !== undefined ? alpha(seedColor!, 0.35) : 'rgba(255,255,255,0.07)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {p.seed !== undefined ? (
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: seedColor!, lineHeight: 1 }}>
                        {p.seed}
                      </Typography>
                    ) : (
                      <SportsTennis sx={{ fontSize: 13, color: alpha(colors.text.secondary, 0.3) }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.87rem', color: overBudget ? alpha(colors.text.primary, 0.5) : colors.text.primary, lineHeight: 1.25 }} noWrap>
                      {p.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.66rem', color: alpha(colors.text.secondary, 0.6), lineHeight: 1.3 }}>
                      {flagEmoji(p.team)} {p.team}
                      {p.points > 0 && (
                        <Box component="span" sx={{ ml: 0.75, color: alpha(accentColor, 0.7), fontWeight: 600 }}>
                          · {p.points}pts
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </Box>

                {/* Right: price + check */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ml: 1 }}>
                  {hasBudget && p.price !== undefined && (
                    <Chip size="small" label={`${p.price}cr`}
                      sx={{
                        height: 20, fontSize: '0.68rem', fontWeight: 700,
                        bgcolor: overBudget ? alpha('#EF4444', 0.1) : isSelected ? alpha(accentColor, 0.18) : alpha(accentColor, 0.1),
                        color: overBudget ? alpha('#EF4444', 0.8) : accentColor,
                        border: `1px solid ${overBudget ? alpha('#EF4444', 0.25) : alpha(accentColor, 0.25)}`,
                      }} />
                  )}
                  {isSelected && <CheckCircle sx={{ fontSize: 17, color: accentColor }} />}
                </Box>
              </ButtonBase>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
          <Button variant="text" onClick={() => { setStep('selectSlot'); setSelectedPlayer(null); }}
            sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
          <Button variant="contained" disabled={!selectedPlayer || isOverBudget}
            onClick={() => selectedPlayer && setStep('confirm')}
            sx={{ flex: 2.5, ...ctaButton(accentColor, !selectedPlayer || isOverBudget) }}>
            Review Transfer
          </Button>
        </Box>
      </>
    );
  };

  const renderSelectPositions = () => (
    <>
      <StepHeader
        title={`Reorder ${gender === 'men' ? "Men's" : "Women's"} Slots`}
        subtitle={swapSlotA === null ? 'Select the first slot' : swapSlotB === null ? 'Now select the second slot' : 'Ready to swap'}
        accent={accentColor}
      />

      {/* Step indicator */}
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.25, pb: 0.5, display: 'flex', gap: 0.75, flexShrink: 0 }}>
        {[0, 1].map(i => (
          <Box key={i} sx={{
            flex: 1, height: 3, borderRadius: 2,
            bgcolor: (i === 0 && swapSlotA !== null) || (i === 1 && swapSlotB !== null)
              ? accentColor : alpha(accentColor, 0.18),
            transition: 'background-color 0.2s ease',
          }} />
        ))}
      </Box>

      <Box sx={{ px: { xs: 1.5, sm: 2 }, py: 1, flex: 1, overflowY: 'auto' }}>
        {Array.from({ length: slotCount }, (_, i) => (
          <SlotCard
            key={i} g={gender} idx={i}
            selected={swapSlotA === i || swapSlotB === i}
            dimmed={swapSlotA !== null && swapSlotA !== i && swapSlotB !== null && swapSlotB !== i}
            onTap={() => {
              if (swapSlotA === null) { setSwapSlotA(i); return; }
              if (i === swapSlotA) { setSwapSlotA(null); return; }
              setSwapSlotB(i);
            }}
          />
        ))}

        {swapSlotA !== null && swapSlotB !== null && (
          <Box sx={{
            mt: 1.5, p: 1.75, borderRadius: 2.5,
            bgcolor: alpha(accentColor, 0.07), border: `1px solid ${alpha(accentColor, 0.22)}`,
            display: 'flex', alignItems: 'center', gap: 1,
          }}>
            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.62rem', color: alpha(accentColor, 0.65), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {slotLabel(gender, swapSlotA)} · {multipliers[swapSlotA]}x
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.text.primary, mt: 0.25 }} noWrap>
                {playerAt(gender, swapSlotA)?.playerName ?? '—'}
              </Typography>
            </Box>
            <Box sx={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              bgcolor: alpha(accentColor, 0.12), border: `1px solid ${alpha(accentColor, 0.3)}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <SwapHoriz sx={{ fontSize: 17, color: accentColor }} />
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.62rem', color: alpha(accentColor, 0.65), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                {slotLabel(gender, swapSlotB)} · {multipliers[swapSlotB]}x
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: colors.text.primary, mt: 0.25 }} noWrap>
                {playerAt(gender, swapSlotB)?.playerName ?? '—'}
              </Typography>
            </Box>
          </Box>
        )}

        {swapSlotA === null && (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1.25, borderRadius: 2, bgcolor: alpha(accentColor, 0.04), border: `1px solid ${alpha(accentColor, 0.1)}` }}>
            <Typography sx={{ fontSize: '0.68rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", lineHeight: 1.5 }}>
              Each player's earned points will be banked and their multiplier clock resets. Your total score is preserved.
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
        <Button variant="text" onClick={() => { setStep('chooseType'); setSwapSlotA(null); setSwapSlotB(null); }}
          sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
          ← Back
        </Button>
        <Button variant="contained" disabled={swapSlotA === null || swapSlotB === null}
          onClick={() => setStep('confirm')}
          sx={{ flex: 2.5, ...ctaButton(accentColor, swapSlotA === null || swapSlotB === null) }}>
          Review Swap
        </Button>
      </Box>
    </>
  );

  const renderConfirm = () => {
    const isPlayerSwap = transferType === 'playerSwap';
    const outgoing     = isPlayerSwap && targetSlot !== null ? playerAt(gender, targetSlot) : null;
    const confirmDisabled = saving || isOverBudget || noTransfersLeft;
    const newSpend = isPlayerSwap ? previewBudget : currentSpend;
    const remainingAfter = budget - newSpend;

    return (
      <>
        <StepHeader title="Confirm Transfer" subtitle="Review before locking in" accent={accentColor} />
        <Box sx={{ p: { xs: 2, sm: 2.5 }, flex: 1, overflowY: 'auto' }}>

          {/* Player swap summary */}
          {isPlayerSwap && outgoing && selectedPlayer ? (
            <Box sx={{ mb: 2 }}>
              {/* OUT row */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.5, py: 1.25, borderRadius: '12px 12px 4px 4px',
                bgcolor: alpha('#EF4444', 0.07), border: `1px solid ${alpha('#EF4444', 0.18)}`,
                borderBottom: 'none',
              }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: alpha('#EF4444', 0.12), border: `1px solid ${alpha('#EF4444', 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: '#EF4444', textTransform: 'uppercase' }}>OUT</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: colors.text.primary, fontSize: '0.9rem' }} noWrap>
                    {outgoing.playerName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.55) }}>
                    {slotLabel(gender, targetSlot!)}
                  </Typography>
                </Box>
                {outgoing.price != null && (
                  <Box sx={{ flexShrink: 0, px: 1, py: 0.4, borderRadius: 1.5, bgcolor: alpha('#EF4444', 0.1), border: `1px solid ${alpha('#EF4444', 0.2)}` }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>
                      {outgoing.price}cr
                    </Typography>
                  </Box>
                )}
              </Box>
              {/* Arrow connector */}
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.25, bgcolor: alpha(accentColor, 0.04), borderLeft: `1px solid ${alpha(accentColor, 0.12)}`, borderRight: `1px solid ${alpha(accentColor, 0.12)}` }}>
                <ArrowForward sx={{ fontSize: 14, color: alpha(accentColor, 0.5), transform: 'rotate(90deg)' }} />
              </Box>
              {/* IN row */}
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.5, py: 1.25, borderRadius: '4px 4px 12px 12px',
                bgcolor: alpha(colors.success.primary, 0.07), border: `1px solid ${alpha(colors.success.primary, 0.18)}`,
                borderTop: 'none',
              }}>
                <Box sx={{ width: 28, height: 28, borderRadius: 1.25, bgcolor: alpha(colors.success.primary, 0.12), border: `1px solid ${alpha(colors.success.primary, 0.25)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: colors.success.primary, textTransform: 'uppercase' }}>IN</Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: colors.text.primary, fontSize: '0.9rem' }} noWrap>
                    {selectedPlayer.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.55) }}>
                    {slotLabel(gender, targetSlot!)}
                    {selectedPlayer.seed !== undefined && (
                      <Box component="span" sx={{ ml: 0.75, color: alpha(colors.orange.light, 0.8) }}>· Seed {selectedPlayer.seed}</Box>
                    )}
                  </Typography>
                </Box>
                {selectedPlayer.price != null && (
                  <Box sx={{ flexShrink: 0, px: 1, py: 0.4, borderRadius: 1.5, bgcolor: alpha(colors.success.primary, 0.1), border: `1px solid ${alpha(colors.success.primary, 0.2)}` }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: colors.success.primary, fontFamily: "'Satoshi', sans-serif" }}>
                      {selectedPlayer.price}cr
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            /* Position swap summary */
            <Box sx={{ ...glassCard(accentColor), p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', color: alpha(accentColor, 0.65), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {slotLabel(gender, swapSlotA!)} → gets {multipliers[swapSlotB!]}x
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.text.primary, mt: 0.3 }} noWrap>
                  {playerAt(gender, swapSlotA!)?.playerName ?? '—'}
                </Typography>
              </Box>
              <Box sx={{ width: 34, height: 34, borderRadius: '50%', flexShrink: 0, bgcolor: alpha(accentColor, 0.12), border: `1px solid ${alpha(accentColor, 0.3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <SwapHoriz sx={{ fontSize: 18, color: accentColor }} />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.62rem', color: alpha(accentColor, 0.65), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {slotLabel(gender, swapSlotB!)} → gets {multipliers[swapSlotA!]}x
                </Typography>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: colors.text.primary, mt: 0.3 }} noWrap>
                  {playerAt(gender, swapSlotB!)?.playerName ?? '—'}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Budget stat — full width */}
          {hasBudget && (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', mb: 2 }}>
              <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(isOverBudget ? '#EF4444' : accentColor, 0.06), border: `1px solid ${alpha(isOverBudget ? '#EF4444' : accentColor, 0.18)}` }}>
                <Typography sx={{ fontSize: '0.6rem', color: alpha(isOverBudget ? '#EF4444' : accentColor, 0.65), fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.25 }}>
                  {isOverBudget ? 'Over budget' : 'Remaining'}
                </Typography>
                <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '1.5rem', color: isOverBudget ? '#EF4444' : accentColor, lineHeight: 1 }}>
                  {isOverBudget ? `-${(newSpend - budget).toFixed(1)}` : remainingAfter.toFixed(1)}cr
                </Typography>
                <Typography sx={{ fontSize: '0.6rem', color: alpha(colors.text.secondary, 0.45), mt: 0.25 }}>
                  of {budget}cr total
                </Typography>
              </Box>
            </Box>
          )}

          {/* Transfers left indicator */}
          {maxTransfers > 0 && !noTransfersLeft && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, px: 1, py: 0.75, borderRadius: 1.75, bgcolor: alpha(colors.text.secondary, 0.04), border: `1px solid rgba(255,255,255,0.06)` }}>
              <TrendingUp sx={{ fontSize: 13, color: alpha(colors.text.secondary, 0.4) }} />
              <Typography sx={{ fontSize: '0.67rem', color: alpha(colors.text.secondary, 0.45), fontFamily: "'Satoshi', sans-serif" }}>
                {transfersLeft - 1 > 0 ? `${transfersLeft - 1} transfer${transfersLeft - 1 !== 1 ? 's' : ''} remaining after this` : 'This is your last transfer'}
              </Typography>
            </Box>
          )}

          {error && (
            <Box sx={{ px: 1.25, py: 1, borderRadius: 2, bgcolor: alpha('#EF4444', 0.09), border: `1px solid ${alpha('#EF4444', 0.25)}`, mb: 1 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#EF4444', fontFamily: "'Satoshi', sans-serif" }}>{error}</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: { xs: 2, sm: 2.5 }, pb: { xs: 3, sm: 2.5 }, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 1 }}>
          <Button variant="text"
            onClick={() => setStep(isPlayerSwap ? 'selectPlayer' : 'selectPositions')}
            disabled={saving}
            sx={{ flex: 1, color: alpha(colors.text.secondary, 0.6), textTransform: 'none', fontFamily: "'Satoshi', sans-serif", borderRadius: 2 }}>
            ← Back
          </Button>
          <Button variant="contained"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            startIcon={saving ? <CircularProgress size={15} color="inherit" sx={{ mr: -0.25 }} /> : <CheckCircle sx={{ fontSize: 17 }} />}
            sx={{ flex: 2.5, ...ctaButton(accentColor, confirmDisabled) }}>
            {saving ? 'Saving…' : 'Confirm Transfer'}
          </Button>
        </Box>
      </>
    );
  };

  // ── Dialog ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: '#080D18',
          backgroundImage: 'none',
          border: { xs: 'none', sm: `1px solid ${alpha(accentColor, 0.2)}` },
          borderRadius: { xs: '20px 20px 0 0', sm: 3 },
          m: { xs: 0, sm: 2 },
          mt: { xs: 'auto', sm: 2 },
          maxHeight: { xs: '92vh', sm: '86vh' },
          display: 'flex',
          flexDirection: 'column',
          // Mobile drag handle hint
          '&::before': {
            content: { xs: '""', sm: 'none' },
            display: 'block',
            width: 36,
            height: 4,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.15)',
            mx: 'auto',
            mt: 1,
            mb: -0.5,
            flexShrink: 0,
          },
        },
      }}
    >
      {step === 'chooseType'      && renderChooseType()}
      {step === 'selectSlot'      && renderSelectSlot()}
      {step === 'selectPlayer'    && renderSelectPlayer()}
      {step === 'selectPositions' && renderSelectPositions()}
      {step === 'confirm'         && renderConfirm()}
    </Dialog>
  );
};

export default TennisTransferModal;
